/**
 * Diff `seedDoc` → `newAdfNode` via `@atlassian/content-reconciler` and
 * dispatch the resulting Steps as one `addToHistory: false` tx. Each Step
 * is rebased through `interveningMapping` (non-own txs since the seed)
 * before dispatch, making the writeback safe to interleave with remote
 * collab steps. The tx carries `OWN_WRITEBACK_META` so the orchestrator's
 * dispatch tap can echo-filter it synchronously.
 */
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Mapping, Step } from '@atlaskit/editor-prosemirror/transform';
import { AttrStep } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { DiffOptions } from '@atlassian/content-reconciler';
import { diffNodes } from '@atlassian/content-reconciler';

/** Meta tag set on every writeback tx so the orchestrator's tap can echo-filter it. */
export const OWN_WRITEBACK_META = 'mdMode$ownWriteback';

/*
	The localId attribute is not produced from the markdown source as it has no concept of localIds.
	This can cause steps to be generated setting the localId attr on existing nodes to null.
	We suppress these as we rarely (if ever) want to actually remove or edit localIds of nodes during
	md -> adf reconciliation.
	This prevents unnecessary round-trips and makes markdown editing more stable.
 */
const shouldIgnoreLocalIdAttrStep = (step: Step): boolean =>
	step instanceof AttrStep && step.attr === 'localId';

export const applyDiffSteps = (
	editorView: EditorView,
	seedDoc: PMNode,
	newAdfNode: PMNode,
	interveningMapping: Mapping,
	opts?: DiffOptions,
): Transaction | null => {
	if (editorView.isDestroyed) {
		return null;
	}

	const steps = diffNodes(seedDoc, newAdfNode, opts).filter(
		(step) => !shouldIgnoreLocalIdAttrStep(step),
	);
	if (steps.length === 0) {
		return null;
	}

	const tr = editorView.state.tr;
	for (const step of steps) {
		const rebased = step.map(interveningMapping);
		if (!rebased) {
			throw new Error('applyDiffSteps: step did not survive rebase');
		}
		const mapped = rebased.map(tr.mapping);
		if (!mapped) {
			throw new Error('applyDiffSteps: step could not be mapped');
		}
		tr.step(mapped);
	}
	tr.setMeta('addToHistory', false);
	tr.setMeta(OWN_WRITEBACK_META, true);
	editorView.dispatch(tr);
	return tr;
};
